// @givmo/app-charity — active-org selection persistence (per-user, per-browser).
//
// The charity portal supports MULTI-ORG admins (fiscal sponsors / consultants manage several orgs — plan
// §2 "Identity & RBAC: multi-charity per user"). The org switcher persists WHICH org the admin last acted
// on, PER USER, on this device — so a reload lands back on the same org instead of snapping to the first
// membership. Single-org admins never see or exercise this (their one org is always the default).
//
// It also persists WHICH ORGS THIS DEVICE HAS ALREADY SEEN in the memberships list, which is what makes
// the switcher's "You now administer X" moment one-time rather than a banner that reappears on every
// render (CPA-WEB46).
//
// ★ THE ORGANIZATION IN FORCE BELONGS TO THE TAB (KCC dev build, 2026-10-31). The selection is kept twice:
// in this tab's `sessionStorage` (what a reload of THIS tab lands on) and in `localStorage` (the last
// choice on this device, which only seeds a tab that has none of its own: a new tab, a new window).
// Kept in `localStorage` alone, a switch in one console tab re-targeted every other tab on its next reload,
// silently: a person working on one organization's emails reloaded into another organization's console.
// A tab's in-memory selection was never shared; only the reload was, and the tab-first read ends that.
//
// Keyed per-UID so two accounts on a shared browser never inherit each other's selection. A persisted id
// is always REVALIDATED against the live membership list before use (see active-org-context) — a stale /
// no-longer-a-member id self-heals to the default. The store is a HINT, never a source of truth.
//
// SSR / privacy-mode guarded exactly like `apps/admin/src/features/onboarding/onboarding-dismissal.ts`:
// every access no-ops (never throws) when `window`/`localStorage` is unavailable (sandboxed iframe,
// Safari private mode).

const KEY_PREFIX = 'givmoCharity.activeOrgId';
const KNOWN_ORGS_KEY_PREFIX = 'givmoCharity.knownOrgIds';

/** Build the exact, per-user localStorage key. A signed-out / uid-less caller uses the `anon` bucket. */
function activeOrgKey(uid: string | null): string {
  return `${KEY_PREFIX}.${uid ?? 'anon'}`;
}

/** The per-user key for the seen-organizations baseline. Same `anon` bucketing as the selection. */
function knownOrgsKey(uid: string | null): string {
  return `${KNOWN_ORGS_KEY_PREFIX}.${uid ?? 'anon'}`;
}

/** The device's localStorage handle, or `null` where storage is blocked/unavailable. Never throws. */
function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** This tab's sessionStorage handle, or `null` where it is blocked/unavailable. Never throws. */
function tabStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readItem(store: Storage | null, key: string): string | null {
  if (store === null) return null;
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

/**
 * The selected charity id for `uid` in THIS tab, else the last one selected on this device (which seeds a
 * tab with no choice of its own), or `null` when neither is held. See the header's ★.
 */
export function readActiveOrgId(uid: string | null): string | null {
  const key = activeOrgKey(uid);
  return readItem(tabStorage(), key) ?? readItem(storage(), key);
}

/**
 * Persist the selected charity id for `uid`: as this tab's choice and as the device's last one. Another
 * tab's choice is never touched: its own `sessionStorage` still answers its reloads. No-op (never throws)
 * where storage is unavailable.
 */
export function writeActiveOrgId(uid: string | null, charityId: string): void {
  const key = activeOrgKey(uid);
  for (const store of [tabStorage(), storage()]) {
    if (store === null) continue;
    try {
      store.setItem(key, charityId);
    } catch {
      // Privacy mode / quota — best-effort; the selection simply won't survive a reload.
    }
  }
}

/**
 * The charity ids this device last saw in `uid`'s memberships list, or `null` when it has never seen
 * one (CPA-WEB46).
 *
 * ⚠ `null` and `[]` mean DIFFERENT things and the caller must keep them apart. `null` is "no baseline
 * yet" — every organization in the list is merely the first sighting, and announcing them would greet
 * an established admin with "You now administer <the org they have had all along>" on their next visit.
 * `[]` is a real baseline recorded for a caller who genuinely administered nothing, so the first
 * approved claim IS news. Storage that is blocked entirely also answers `null`, which fails toward
 * silence — the right direction for a moment nobody asked for.
 */
export function readKnownOrgIds(uid: string | null): string[] | null {
  const store = storage();
  if (store === null) return null;
  try {
    const raw = store.getItem(knownOrgsKey(uid));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    // Tolerant reader: a hand-edited or half-written value is treated as no baseline, never a throw.
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string')
      : null;
  } catch {
    return null;
  }
}

/** Record the charity ids currently in `uid`'s memberships list. No-op (never throws) when unavailable. */
export function writeKnownOrgIds(uid: string | null, charityIds: readonly string[]): void {
  const store = storage();
  if (store === null) return;
  try {
    store.setItem(knownOrgsKey(uid), JSON.stringify(charityIds));
  } catch {
    // Privacy mode / quota — best-effort; the moment simply won't be suppressed on a later visit.
  }
}
